import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, ExternalLinkIcon, MenuIcon, PhoneCallIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { phones } from '../data/contact';
const ease = [0.22, 1, 0.36, 1] as const;
const fade = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  transition: {
    duration: 0.35,
    ease
  }
};
export function Navbar() {
  const {
    language,
    t
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [officesOpen, setOfficesOpen] = useState(false);
  const [mobileOfficesOpen, setMobileOfficesOpen] = useState(false);
  const officesRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (officesRef.current && !officesRef.current.contains(event.target as Node)) {
        setOfficesOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOfficesOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);
  const countryProps = (external: boolean) => external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {};
  return <header className="fixed inset-x-0 top-0 z-50" data-id="element-28" data-mp="34de869wooe1i">
      <div className={`transition-all duration-300 ${scrolled ? 'glass border-b border-ink/10 shadow-soft' : 'border-b border-transparent bg-transparent'}`} data-id="element-29" data-mp="34as5ankqmmpm">
        <nav className="mx-auto flex h-[72px] max-w-shell items-center justify-between px-5 sm:px-8" aria-label={t.nav.ariaLabel} data-id="element-30" data-mp="319go73biejv1">
          <Logo data-id="element-31" data-mp="3b1cnuit8ui1d" />

          <motion.ul key={`links-${language}`} {...fade} className="hidden shrink-0 items-center gap-0.5 lg:flex xl:gap-1" data-id="element-32" data-mp="3azrsws30ox2z">
            {t.nav.links.map(link => <li key={link.href} data-id="element-33" data-mp="2icnr0vz80yvf">
                <a href={link.href} className="block whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium text-ink/70 transition-colors hover:bg-ink/[0.04] hover:text-ink xl:px-3.5 xl:text-[14px]" data-id="element-34" data-mp="2ddkkt87i8mlv">
                  {link.label}
                </a>
              </li>)}

            <li className="relative" ref={officesRef} data-id="element-35" data-mp="7it0nupfa2df">
              <button type="button" onClick={() => setOfficesOpen(v => !v)} aria-expanded={officesOpen} aria-haspopup="true" className="flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium text-ink/70 transition-colors hover:bg-ink/[0.04] hover:text-ink xl:px-3.5 xl:text-[14px]" data-id="element-36" data-mp="2ljceidrrwr6">
                {t.nav.offices.label}
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ${officesOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-37" data-mp="55za8oqjueip" />
              </button>

              <AnimatePresence data-id="element-38" data-mp="xw7my12bg6pw">
                {officesOpen && <motion.ul initial={{
                opacity: 0,
                y: -8,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -8,
                scale: 0.98
              }} transition={{
                duration: 0.22,
                ease
              }} className="glass absolute right-0 top-[calc(100%+10px)] w-[240px] overflow-hidden rounded-3xl border border-ink/10 p-2 shadow-lift" data-id="element-39" data-mp="k2gi35zj5b0i">
                    {t.nav.offices.countries.map(country => <li key={country.name} data-id="element-40" data-mp="3k4x6qtpm9nij">
                        <a href={country.href} {...countryProps(country.external)} onClick={() => setOfficesOpen(false)} className="group flex items-center gap-3 rounded-2xl px-3.5 py-3 transition-colors duration-200 hover:bg-ink/[0.05]" data-id="element-41" data-mp="3f2zghfistsg3">
                          <span className="text-[18px] leading-none" aria-hidden="true" data-id="element-42" data-mp="1x7qz0hgqfvo">
                            {country.flag}
                          </span>
                          <span className="flex-1 text-[14px] font-medium text-ink/80 group-hover:text-ink" data-id="element-43" data-mp="3u0id141px9y3">
                            {country.name}
                          </span>
                          {country.external && <ExternalLinkIcon className="h-3.5 w-3.5 text-ink/35 transition-colors group-hover:text-brand" aria-hidden="true" data-id="element-44" data-mp="289jtjagh1flo" />}
                        </a>
                      </li>)}
                  </motion.ul>}
              </AnimatePresence>
            </li>
          </motion.ul>

          <div className="flex shrink-0 items-center gap-2 xl:gap-3" data-id="element-45" data-mp="2r7mos6zm48rx">
            <LanguageSwitcher data-id="element-46" data-mp="2w6z6kdbmmpnn" />
            <motion.div key={`cta-${language}`} {...fade} className="hidden items-center gap-2 md:flex xl:gap-3" data-id="element-47" data-mp="2w67lvxjkicc5">
              <a href={phones[0].href} aria-label={phones[0].display} title={phones[0].display} className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/10 text-ink/70 transition-colors hover:border-ink/25 hover:text-ink lg:flex" data-id="element-48" data-mp="3502igh6miug6">
                <PhoneCallIcon className="h-4 w-4" aria-hidden="true" data-id="element-49" data-mp="1eto6ox0eavl4" />
              </a>
              <a href="#consultation" className="whitespace-nowrap rounded-full bg-brand px-4 py-2.5 text-[13px] font-semibold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-600 xl:px-5 xl:text-[14px]" data-id="element-50" data-mp="19yh8u5tt4l44">
                {t.nav.cta}
              </a>
            </motion.div>

            <button type="button" onClick={() => setOpen(v => !v)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-ink/10 text-ink lg:hidden" aria-label={open ? t.nav.closeMenu : t.nav.openMenu} aria-expanded={open} data-id="element-51" data-mp="1tleuw9efn79c">
              {open ? <XIcon className="h-5 w-5" data-id="element-52" data-mp="d6nc8o739ed8" /> : <MenuIcon className="h-5 w-5" data-id="element-53" data-mp="87k8r86rbg7d" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence data-id="element-54" data-mp="2juxfu34m8a2r">
        {open && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.25
      }} className="glass max-h-[calc(100vh-72px)] overflow-y-auto border-b border-ink/10 px-5 pb-6 pt-2 shadow-lift lg:hidden" data-id="element-55" data-mp="2jv5vg2s4hthn">
            <ul className="flex flex-col" data-id="element-56" data-mp="21462yrlf61jm">
              {t.nav.links.map(link => <li key={link.href} data-id="element-57" data-mp="216irjnlona5n">
                  <a href={link.href} onClick={() => setOpen(false)} className="block border-b border-ink/5 py-3.5 text-[15px] font-medium text-ink" data-id="element-58" data-mp="263iz62y6kkyv">
                    {link.label}
                  </a>
                </li>)}

              <li className="border-b border-ink/5" data-id="element-59" data-mp="3llaydl5xgsm0">
                <button type="button" onClick={() => setMobileOfficesOpen(v => !v)} aria-expanded={mobileOfficesOpen} className="flex w-full items-center justify-between py-3.5 text-left text-[15px] font-medium text-ink" data-id="element-60" data-mp="3qiudenag99f0">
                  {t.nav.offices.label}
                  <ChevronDownIcon className={`h-4 w-4 text-ink/50 transition-transform duration-300 ${mobileOfficesOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-61" data-mp="24id1z5mllmii" />
                </button>
                <AnimatePresence initial={false} data-id="element-62" data-mp="1qjde406wtbne">
                  {mobileOfficesOpen && <motion.ul initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.28,
                ease
              }} className="overflow-hidden" data-id="element-63" data-mp="1llv03ve1c68j">
                      {t.nav.offices.countries.map(country => <li key={country.name} data-id="element-64" data-mp="3iiznbc6bu3ok">
                          <a href={country.href} {...countryProps(country.external)} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-2xl px-3 py-3 text-[15px] font-medium text-ink/75 transition-colors hover:bg-ink/[0.04] hover:text-ink" data-id="element-65" data-mp="3nic8ztlgp4pk">
                            <span className="text-[18px] leading-none" aria-hidden="true" data-id="element-66" data-mp="34jhsaf2ftu6r">
                              {country.flag}
                            </span>
                            <span className="flex-1" data-id="element-67" data-mp="39kxdc74m80p5">{country.name}</span>
                            {country.external && <ExternalLinkIcon className="h-3.5 w-3.5 text-ink/35" aria-hidden="true" data-id="element-68" data-mp="osvkfs19yooo" />}
                          </a>
                        </li>)}
                      <li className="h-2" aria-hidden="true" data-id="element-69" data-mp="cxyamjouruhm" />
                    </motion.ul>}
                </AnimatePresence>
              </li>
            </ul>

            <a href="#consultation" onClick={() => setOpen(false)} className="mt-5 block rounded-2xl bg-brand px-5 py-3.5 text-center text-[15px] font-semibold text-white shadow-glow" data-id="element-70" data-mp="lpqhkkeo4lhg">
              {t.nav.mobileCta}
            </a>
          </motion.div>}
      </AnimatePresence>
    </header>;
}